import React, { useEffect, useRef, useState } from 'react';
import LightRays from '../../components/common/LightRays';
import FlowingMenu from '../../components/common/FlowingMenu';
import ins1 from '../../assets/images/ins1.png';
import ins2 from '../../assets/images/ins2.png';
import ins3 from '../../assets/images/ins3.png';
import ins4 from '../../assets/images/ins4.png';
import ins5 from '../../assets/images/ins5.png';
import ins6 from '../../assets/images/ins6.png';
import ins7 from '../../assets/images/ins7.png';
import SEO from '../../components/common/SEO';

function WhyCard({ card, visible }) {
  const cardRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0, inside: false });

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    setCursor({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      inside: true,
    });
  };

  const handleMouseLeave = () => {
    setCursor(prev => ({ ...prev, inside: false }));
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        background: '#ffffff',
        borderRadius: '20px',
        padding: '24px 20px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        cursor: 'default',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(50px)',
        transition: `opacity 0.7s ease ${card.delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${card.delay}ms`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(30,58,138,0.12) 0%, transparent 70%)',
          transform: 'translate(-50%, -50%)',
          left: cursor.x,
          top: cursor.y,
          opacity: cursor.inside ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '3px',
          width: cursor.inside ? '100%' : '0%',
          background: 'linear-gradient(to right, #1e3a8a, #facc15)',
          transition: 'width 0.4s ease',
          borderRadius: '0 0 20px 20px',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: cursor.inside ? '#0d2240' : '#eef4ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
            transition: 'background 0.3s ease',
            color: cursor.inside ? '#facc15' : '#1e3a8a',
          }}
        >
          <div style={{ transform: 'scale(0.85)' }}>
            {card.icon}
          </div>
        </div>

        <h3
          style={{
            fontSize: '15px',
            fontWeight: 700,
            color: '#0d2240',
            marginBottom: '8px',
            lineHeight: 1.3,
          }}
        >
          {card.title}
        </h3>

        <p
          style={{
            fontSize: '13px',
            color: '#64748b',
            lineHeight: 1.6,
          }}
        >
          {card.text}
        </p>
      </div>
    </div>
  );
}


function AboutUs() {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach(key => {
      const element = sectionRefs.current[key];
      if (!element) return;
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setIsVisible(prev => ({ ...prev, [key]: true }));
        },
        { threshold: 0.05 }
      );
      observers[key].observe(element);
    });
    return () => Object.values(observers).forEach(o => o.disconnect());
  }, []);

  return (
    <div className="bg-white">
      <SEO title="About Us" description="Learn about Foundation Solar Energy Limited, our history, and our core values." url="/about" />

      {/* hero section */}
      <section
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '500px',
          background: 'linear-gradient(135deg, #0a1628 0%, #0d2240 40%, #0f2d5a 70%, #0a1f44 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <LightRays
            raysOrigin="top-center"
            raysColor="#ffffff"
            raysSpeed={2.2}
            lightSpread={2}
            rayLength={3}
            followMouse={true}
            mouseInfluence={0.5}
            noiseAmount={0.2}
            distortion={0}
            className="custom-rays"
            pulsating={false}
            fadeDistance={1}
            saturation={1.6}
          />
        </div>

        <div
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px',
            background: 'linear-gradient(to bottom, transparent, rgba(10,31,68,0.6))',
            zIndex: 2,
          }}
        />

        <div
          style={{ position: 'relative', zIndex: 3, textAlign: 'center', padding: '60px 20px' }}
          className="max-w-4xl mx-auto"
        >
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6"
            style={{ letterSpacing: '-0.02em', lineHeight: 1.1 }}
          >
            About Us
          </h1>
          <div
            className="mx-auto mb-6 sm:mb-8"
            style={{
              width: '60px', 
              height: '3px',
              background: 'linear-gradient(to right, #3b82f6, #facc15)',
              borderRadius: '2px',
            }}
          />
          <p
            className="text-blue-100 leading-relaxed mx-auto text-sm sm:text-base lg:text-lg px-4"
            style={{ maxWidth: '780px', opacity: 0.9 }}
          >
            Foundation Solar Energy (Pvt) Limited (FSEL) is a rapidly growing force in
            Pakistan's renewable energy landscape, established in 2019 with a bold vision
            to reshape national energy infrastructure. While our roots are firmly planted
            in the 70-year industrial legacy of the Fauji Foundation Group, we operate
            with the agility and "full force" of a modern technology leader. From our
            initial takeoff in 2020 to achieving nearly 100 MW in project milestones
            today, we have expanded nationwide to serve industrial and utility-scale needs.
            We pride ourselves on being a "Sun to Socket" specialist, combining high-level
            engineering with a deep commitment to environmental impact and economic value
            for the communities we serve.
          </p>
        </div>
      </section>

      {/* core values */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Our Core Values</h2>
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 mx-auto"></div>
        </div>
        <div style={{ height: '400px', position: 'relative' }} className="sm:h-[480px]">
          <FlowingMenu
            items={[
              { link: '#', text: 'Integrity',      image: ins1 },
              { link: '#', text: 'Nationalism',    image: ins2 },
              { link: '#', text: 'Safety',         image: ins3 },
              { link: '#', text: 'Passion',        image: ins4 },
              { link: '#', text: 'Innovation',     image: ins5 },
              { link: '#', text: 'Responsibility', image: ins6 },
              { link: '#', text: 'Excellence',     image: ins7 },
            ]}
            speed={15}
            textColor="#1e3a5f"
            bgColor="#f0f6ff"
            marqueeBgColor="#1e3a5f"
            marqueeTextColor="#ffffff"
            borderColor="#bfdbfe"
          />
        </div>
      </section>

      {/* mission and vision */}
      <section className="overflow-hidden">

        <div
          className="relative"
          style={{ background: '#0d2240' }}
          ref={el => sectionRefs.current['mission'] = el}
        >
          <div style={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, #facc15, transparent)' }} />

          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-16 py-12 sm:py-16 flex flex-col sm:flex-row gap-6 sm:gap-12 items-start relative z-10"
            style={{
              opacity: isVisible['mission'] ? 1 : 0,
              transform: isVisible['mission'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.9s ease, transform 0.9s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            <span style={{
              fontSize: 'clamp(48px, 15vw, 120px)', 
              fontWeight: 700,
              color: 'rgba(250,204,21,0.08)', 
              lineHeight: 1, 
              flexShrink: 0,
              marginTop: '-8px', 
              userSelect: 'none', 
              letterSpacing: '-4px',
            }}>
              01
            </span>
            <div style={{ paddingTop: '6px' }}>
              <p style={{
                fontSize: '10px', 
                fontWeight: 600, 
                letterSpacing: '0.18em',
                textTransform: 'uppercase', 
                color: '#facc15', 
                marginBottom: '12px',
              }} className="sm:text-xs">
                Our Mission
              </p>
              <h2 className="text-white mb-3 sm:mb-4 text-2xl sm:text-3xl lg:text-4xl" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                Driving Environmental Impact{' '}
                <span style={{ color: '#facc15' }}>& Economic Value</span>
              </h2>
              <div style={{ width: '40px', height: '3px', background: '#facc15', borderRadius: '2px', marginBottom: '16px' }} />
              <p className="text-sm sm:text-base" style={{ color: '#bfdbfe', lineHeight: 1.75, maxWidth: '600px' }}>
                Our mission is simple yet powerful: to deliver high-quality, cost-effective, and reliable
                technology solutions that drive both environmental impact and economic value. We strive for
                operating excellence and technology innovation to meet the market's evolving energy needs.
              </p>
            </div>
          </div>
        </div>

        <div style={{ height: '2px', background: 'linear-gradient(to right, #facc15 0%, #1e40af 50%, #0d2240 100%)' }} />

        <div
          className="relative"
          style={{ background: '#102a50' }}
          ref={el => sectionRefs.current['vision'] = el}
        >
          <div style={{
            position: 'absolute', inset: 0, opacity: 0.05,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, transparent, #facc15)' }} />

          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-16 py-12 sm:py-16 flex flex-col sm:flex-row gap-6 sm:gap-12 items-start relative z-10"
            style={{
              opacity: isVisible['vision'] ? 1 : 0,
              transform: isVisible['vision'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.9s ease 0.15s, transform 0.9s cubic-bezier(0.22,1,0.36,1) 0.15s',
            }}
          >
            <span style={{
              fontSize: 'clamp(48px, 15vw, 120px)', 
              fontWeight: 700,
              color: 'rgba(250,204,21,0.08)', 
              lineHeight: 1, 
              flexShrink: 0,
              marginTop: '-8px', 
              userSelect: 'none', 
              letterSpacing: '-4px',
            }}>
              02
            </span>
            <div style={{ paddingTop: '6px' }}>
              <p style={{
                fontSize: '10px', 
                fontWeight: 600, 
                letterSpacing: '0.18em',
                textTransform: 'uppercase', 
                color: '#facc15', 
                marginBottom: '12px',
              }} className="sm:text-xs">
                Our Vision
              </p>
              <h2 className="text-white mb-3 sm:mb-4 text-2xl sm:text-3xl lg:text-4xl" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                Leading the Energy{' '}
                <span style={{ color: '#facc15' }}>Innovation Field</span>
              </h2>
              <div style={{ width: '40px', height: '3px', background: '#facc15', borderRadius: '2px', marginBottom: '16px' }} />
              <p className="text-sm sm:text-base" style={{ color: '#bfdbfe', lineHeight: 1.75, maxWidth: '600px' }}>
                To lead the energy innovation field by supporting communities, commercial entities, and the
                industrial sector in strengthening their positioning through renewable energy, energy
                efficiency, and smart grid solutions. We aim to be the premier partner in producing
                sufficient sustainable energy to support global economic growth.
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* why choose us */}
      <section
        className="py-12 sm:py-16 lg:py-20 bg-gray-50 overflow-hidden"
        ref={el => sectionRefs.current['whychoose'] = el}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div
            className="text-center mb-10 sm:mb-14"
            style={{
              opacity: isVisible['whychoose'] ? 1 : 0,
              transform: isVisible['whychoose'] ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5">
            {[
              {
                delay: 0,
                icon: (
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <rect x="8" y="28" width="48" height="28" rx="3"/>
                    <path d="M8 36h48M20 36v20M32 36v20M44 36v20"/>
                    <path d="M16 28V20a16 16 0 0132 0v8"/>
                    <circle cx="32" cy="14" r="4" fill="currentColor" stroke="none" opacity="0.3"/>
                  </svg>
                ),
                title: 'Industry-Leading Technology',
                text: 'Cutting-edge innovations for higher efficiency and reliability across all solar installations.',
              },
              {
                delay: 80,
                icon: (
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <circle cx="32" cy="32" r="22"/>
                    <path d="M32 10c0 0-10 8-10 22s10 22 10 22"/>
                    <path d="M32 10c0 0 10 8 10 22s-10 22-10 22"/>
                    <path d="M10 32h44"/>
                    <path d="M13 20h38M13 44h38"/>
                  </svg>
                ),
                title: 'Sustainability Commitment',
                text: 'Reducing carbon emissions and promoting green energy adoption nationwide.',
              },
              {
                delay: 160,
                icon: (
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <path d="M12 52L24 32l10 12 8-16 10 24"/>
                    <circle cx="24" cy="20" r="6"/>
                    <path d="M30 17l6-5M34 22l8 2"/>
                  </svg>
                ),
                title: 'Expertise & Experience',
                text: 'A proven track record of successful solar projects nationwide since 2019.',
              },
              {
                delay: 240,
                icon: (
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <path d="M32 8l4 12h13l-10.5 7.6 4 12L32 32l-10.5 7.6 4-12L15 20h13z"/>
                    <path d="M32 40v16M22 52h20"/>
                  </svg>
                ),
                title: 'Customer-Centric Approach',
                text: 'Tailored solutions built around your specific energy needs, budget, and timeline.',
              },
              {
                delay: 320,
                icon: (
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <path d="M32 12v8M44 16l-5.6 5.6M48 28h-8M44 40l-5.6-5.6M32 44v8M20 40l5.6-5.6M16 28h8M20 16l5.6 5.6"/>
                    <circle cx="32" cy="28" r="8"/>
                    <circle cx="32" cy="28" r="3" fill="currentColor" stroke="none" opacity="0.4"/>
                  </svg>
                ),
                title: 'End-to-End EPC Services',
                text: 'From engineering and procurement to construction and maintenance — all under one roof.',
              },
            ].map((card, i) => (
              <WhyCard key={i} card={card} visible={isVisible['whychoose']} />
            ))}
          </div>

        </div>
      </section>

    </div>
  );
}

export default AboutUs;
